import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion } from 'motion/react';
import { MobileAllowedAreaSlide } from '../mobile/MobileAllowedAreaSlide';
import type { AllowedAreas } from '@/features/booking/types/eventBookingCategoryTypes';

interface DesktopAllowedAreaSectionProps {
  allowedAreas: AllowedAreas | null;
}

export const DesktopAllowedAreaSection: React.FC<DesktopAllowedAreaSectionProps> = ({ allowedAreas }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Check scroll capability
  const checkScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1); // -1 tolerance
    }
  }, []);

  // Listen for resize and scroll events
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      // Check initially
      checkScroll();
      
      const observer = new ResizeObserver(checkScroll);
      observer.observe(container);
      
      return () => {
        container.removeEventListener('scroll', checkScroll);
        observer.disconnect();
      };
    }
  }, [checkScroll, allowedAreas]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400; // scroll by ~1.5 cards
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  // Mouse drag handlers
  const onMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const onMouseLeave = () => {
    if (!isDragging) return;
    setIsDragging(false);
  };

  const onMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2; // scroll-fast
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  if (!allowedAreas || allowedAreas.length === 0) {
    return null;
  }

  const flattenedSlides = React.useMemo(() => {
    const slides: { area: typeof allowedAreas[0]; imageId: string }[] = [];
    allowedAreas.forEach((area) => {
      if (area.image_ids && area.image_ids.length > 0) {
        area.image_ids.forEach((imageId) => {
          slides.push({ area, imageId });
        });
      } else {
        slides.push({ area, imageId: '' });
      }
    });
    return slides;
  }, [allowedAreas]);

  return (
    <section className="w-full py-8 mt-4">
      <div className="px-5 flex items-center justify-between mb-6">
         <h2 className="text-2xl font-bold font-serif text-white tracking-tight">Accessible Areas</h2>
         
         {/* Desktop Navigation Arrows (Top Right) */}
         <div className="hidden sm:flex items-center gap-2">
           <button
             onClick={() => scroll('left')}
             disabled={!canScrollLeft}
             className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
               canScrollLeft 
                 ? 'bg-gray-800 hover:bg-gray-700 text-white border border-gray-600 cursor-pointer hover:scale-105 active:scale-95 shadow-md' 
                 : 'bg-gray-900 text-gray-600 border border-gray-800 cursor-not-allowed'
             }`}
             aria-label="Scroll left"
           >
             <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
             </svg>
           </button>
           <button
             onClick={() => scroll('right')}
             disabled={!canScrollRight}
             className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
               canScrollRight 
                 ? 'bg-gray-800 hover:bg-gray-700 text-white border border-gray-600 cursor-pointer hover:scale-105 active:scale-95 shadow-md' 
                 : 'bg-gray-900 text-gray-600 border border-gray-800 cursor-not-allowed'
             }`}
             aria-label="Scroll right"
           >
             <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
             </svg>
           </button>
         </div>
      </div>
      
      <div className="relative w-full group/scroll">
        {/* Mobile Navigation Arrows (Overlay Left/Right) */}
        {canScrollLeft && (
          <div className="sm:hidden absolute left-0 top-0 bottom-0 w-16 flex items-center justify-start z-10 bg-gradient-to-r from-black via-black/80 to-transparent pointer-events-none transition-opacity duration-300">
            <button
              onClick={() => scroll('left')}
              className="pointer-events-auto ml-2 w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 text-white border border-gray-700 transition-transform hover:scale-105 active:scale-95 flex items-center justify-center shadow-lg text-[0px]"
              aria-label="Scroll left"
            >
              <svg className="w-5 h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          </div>
        )}

        {canScrollRight && (
          <div className="sm:hidden absolute right-0 top-0 bottom-0 w-16 flex items-center justify-end z-10 bg-gradient-to-l from-black via-black/80 to-transparent pointer-events-none transition-opacity duration-300">
            <button
              onClick={() => scroll('right')}
              className="pointer-events-auto mr-2 w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 text-white border border-gray-700 transition-transform hover:scale-105 active:scale-95 flex items-center justify-center shadow-lg text-[0px]"
              aria-label="Scroll right"
            >
              <svg className="w-5 h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}

        {/* Scrollable Container */}
        <div 
          ref={scrollContainerRef}
          onMouseDown={onMouseDown}
          onMouseLeave={onMouseLeave}
          onMouseUp={onMouseUp}
          onMouseMove={onMouseMove}
          className={`overflow-x-auto flex gap-5 px-5 pb-8 pt-4 scrollbar-hide w-full ${isDragging ? 'cursor-grabbing select-none' : 'cursor-grab'}`}
          style={{ scrollBehavior: isDragging ? 'auto' : 'smooth', msOverflowStyle: 'none', scrollbarWidth: 'none' }}
        >
          {flattenedSlides.map((slide, index) => (
            <motion.div 
              key={`slide-${index}`}
              whileHover={{ 
                y: -4,
                scale: 1.01,
                transition: { duration: 0.3, ease: 'easeOut' }
              }}
              whileTap={{ scale: 0.99 }}
              className="flex-none w-[280px] sm:w-[320px] lg:w-[380px] h-[220px] sm:h-[260px] lg:h-[300px] rounded-2xl overflow-hidden shadow-lg border border-white/10 hover:border-white/20 hover:shadow-[0_10px_25px_-10px_rgba(255,255,255,0.1)] pointer-events-none"
            >
              <MobileAllowedAreaSlide area={slide.area} imageId={slide.imageId} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
